import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import React, { forwardRef, useState, useMemo, useEffect } from 'react';
import { th } from '@xstyled/system';
import styled, { css } from '@xstyled/styled-components';
import Downshift from 'downshift';
import matchSorter from 'match-sorter';
import { CaretDownIcon } from '@trybeapp/ui.icons.caret-down';
import { ClearButton } from '@trybeapp/ui.clear-button';
import { StyledIcon as StyledIcon$1 } from '@trybeapp/ui.icon';
import { filterFieldComponent, wrapperSystem, componentSystem } from '@trybeapp/ui.system';
import { cardStyles, overflowEllipsis, fieldStyles, centerContent, createEvent } from '@trybeapp/ui.utilities';
import 'prop-types';
import { Tag, StyledTag } from '@trybeapp/ui.tag';

var _templateObject;
var StyledTags = /*#__PURE__*/styled.div(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  margin-top: lg;\n\n  ", ":not(:last-child) {\n    margin-right: sm;\n    margin-bottom: sm;\n  }\n\n  &:empty {\n    display: none;\n  }\n"])), /* sc-selector */StyledTag);
var MultipleSelections = function MultipleSelections(values, handleRemove) {
  return /*#__PURE__*/React.createElement(StyledTags, null, values.map(function (tag) {
    return /*#__PURE__*/React.createElement(Tag, {
      key: tag.value,
      onRemove: function onRemove() {
        return handleRemove(tag.value);
      }
    }, tag.label);
  }));
};

var EMPTY_STRING = '';
var kebabCase = function kebabCase(str) {
  var type = typeof str;
  if (type === 'number') {
    return String(str);
  }
  if (type !== 'string') {
    return str;
  }
  var match = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g);
  return match && match.map(function (x) {
    return x.toLowerCase();
  }).join('-');
};
var itemToString = function itemToString(item) {
  return item ? item.label : EMPTY_STRING;
};
var getSpacer = function getSpacer(options) {
  return options.reduce(function (prev, current) {
    return current.label && prev.length > current.label.length ? prev : current.label;
  }, EMPTY_STRING);
};
var getUniqueValue = function getUniqueValue(item, values, allowUnselectFromList) {
  var isExisting = values.find(function (value) {
    return item.value === value.value;
  });
  if (isExisting && allowUnselectFromList) {
    return values.filter(function (value) {
      return item.value !== value.value;
    });
  }
  return isExisting ? values : [].concat(values, [item]);
};
var isValueSelected = function isValueSelected(value, options) {
  return !!options.find(function (item) {
    return item.value === value;
  });
};
var getOption = function getOption(value, options) {
  if (options === void 0) {
    options = [];
  }
  var option = options.find(function (option) {
    return option.label === (value.label || value) || option.value === (value.value || value);
  });
  // Create the option if it doesn't exist
  return option || {
    value: kebabCase(value),
    label: value
  };
};
var getOptionsFromSelected = function getOptionsFromSelected(selected, options) {
  if (!selected && selected !== 0) {
    return [];
  } else if (Array.isArray(selected)) {
    return selected.map(function (value) {
      return getOption(value, options);
    });
  } else {
    return [getOption(selected, options)];
  }
};
var getIsExisting = function getIsExisting(option, options) {
  return !!options.find(function (item) {
    return item.value === option.value;
  });
};
var getValue = function getValue(option, options) {
  return getIsExisting(option, options) ? option.value : option.label;
};
var getValuesFromOptions = function getValuesFromOptions(selected, options) {
  if (!selected) {
    return;
  }
  return selected.map(function (selected) {
    return getValue(selected, options);
  });
};
var getNewOptions = function getNewOptions(selected, options) {
  if (!selected) {
    return;
  }
  // Find selected items that aren't in original items
  return selected.filter(function (item) {
    return !options.find(function (option) {
      return option.value === item.value;
    });
  });
};
var getInputValue = function getInputValue(_ref) {
  var inputValue = _ref.inputValue,
    isMultiple = _ref.isMultiple,
    isSearchable = _ref.isSearchable,
    options = _ref.options,
    renderItem = _ref.renderItem;
  var option = getOption(inputValue, options);
  if (isMultiple) {
    return inputValue;
  }
  if (option.label) {
    if (isSearchable) {
      return option.label;
    } else {
      return renderItem(option);
    }
  }
  return EMPTY_STRING;
};

var _excluded = ["autoComplete", "autoFocus", "dataTestId", "disabled", "icon", "id", "isClearable", "isCreatable", "isMultiple", "isSearchable", "options", "name", "onBlur", "onChange", "onClick", "onCreate", "onFocus", "onKeyDown", "placeholder", "renderItem", "renderMultiple", "size", "type", "value", "variant", "allowUnselectFromList", "disableCloseOnSelect"];
var _templateObject$1, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10;
var StyledWrapper = /*#__PURE__*/styled(filterFieldComponent('div'))(function (_ref) {
  var connected = _ref.connected;
  return css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteralLoose(["\n    position: relative;\n    ", ";\n  "])), !connected && wrapperSystem);
});
var StyledInputWrapper = /*#__PURE__*/styled.div(_templateObject2 || (_templateObject2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n"])));
var FILTER_PROPS = ['hasIcon', 'inputValue', 'renderMultiple'];
var StyledInput = /*#__PURE__*/styled(filterFieldComponent('div', FILTER_PROPS))(function (_ref2) {
  var hasIcon = _ref2.hasIcon,
    size = _ref2.size;
  return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n    position: relative;\n    ", ";\n    ", ";\n    padding-right: ", ";\n    ", ";\n    cursor: default;\n    ", "\n\n    br {\n      display: none;\n    }\n\n    &::before {\n      content: attr(data-spacer);\n      visibility: hidden;\n      display: block;\n      height: 0;\n    }\n\n    &:empty {\n      &::after {\n        content: attr(placeholder);\n        position: absolute;\n        top: 0;\n        right: 0;\n        bottom: 0;\n        left: 0;\n        ", ";\n        padding: inherit;\n        opacity: 0.5;\n        display: flex;\n        align-items: center;\n      }\n      &::before {\n        height: auto;\n      }\n    }\n  "])), fieldStyles, overflowEllipsis, th("fields.sizes." + size + ".height"), hasIcon && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n        padding-left: ", ";\n      "])), th("fields.sizes." + size + ".height")), componentSystem, overflowEllipsis);
});
var ListStyles = function ListStyles() {
  return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", ";\n  position: absolute;\n  z-index: 2;\n  right: 0;\n  left: 0;\n  margin: 0;\n  margin-top: md;\n  padding: 0;\n  transition: medium;\n  overflow: auto;\n  -webkit-overflow-scrolling: touch;\n"])), th('fields.select.default'), cardStyles);
};
var StyledMenu = /*#__PURE__*/styled.ul(_templateObject6 || (_templateObject6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n"])), /*#__PURE__*/ListStyles());
var StyledListItem = /*#__PURE__*/styled.li(function (_ref3) {
  var allowUnselectFromList = _ref3.allowUnselectFromList,
    isHighlighted = _ref3.isHighlighted,
    isMultiple = _ref3.isMultiple,
    isSelected = _ref3.isSelected;
  return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteralLoose(["\n    ", ";\n    ", ";\n    ", ";\n    ", ";\n    padding: md;\n    list-style: none;\n    text-decoration: none;\n    transition: background ", ";\n  "])), isHighlighted && th('fields.select.highlighted'), isSelected && !isMultiple && th('fields.select.selected'), isSelected && isMultiple && !allowUnselectFromList && th('fields.select.existing'), overflowEllipsis, th.transition('medium'));
});
var StyledIcon = /*#__PURE__*/styled.div(function (_ref4) {
  var size = _ref4.size;
  return css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    width: ", ";\n    padding: 0;\n    top: 0;\n    bottom: 0;\n    left: 0;\n    ", "\n  "])), th("fields.sizes." + size + ".height"), centerContent);
});
var StyledIndicators = /*#__PURE__*/styled.div(_templateObject9 || (_templateObject9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  padding: 0;\n  top: 0;\n  bottom: 0;\n  right: 0;\n  display: flex;\n"])));
var StyledDropDownIndicator = /*#__PURE__*/styled.button(function (_ref5) {
  var isOpen = _ref5.isOpen,
    size = _ref5.size;
  return css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteralLoose(["\n    position: relative;\n    height: 100%;\n    width: ", ";\n    padding: 0;\n    outline: none !important; /* important for firefox */\n    appearance: none;\n    cursor: pointer;\n    border: none;\n    background: transparent;\n    ", ";\n\n    ", " {\n      transform: ", ";\n      transition: medium;\n    }\n\n    &:not(:last-child) {\n      width: auto;\n    }\n  "])), th("fields.sizes." + size + ".height"), centerContent, StyledIcon$1, isOpen ? 'rotate3d(0, 0, 1, 180deg)' : 'rotate3d(0)');
});
var Select = /*#__PURE__*/forwardRef(function (_ref6, ref) {
  var _ref6$autoComplete = _ref6.autoComplete,
    autoComplete = _ref6$autoComplete === void 0 ? 'off' : _ref6$autoComplete,
    autoFocus = _ref6.autoFocus,
    dataTestId = _ref6.dataTestId,
    disabled = _ref6.disabled,
    icon = _ref6.icon,
    id = _ref6.id,
    isClearable = _ref6.isClearable,
    isCreatable = _ref6.isCreatable,
    isMultiple = _ref6.isMultiple,
    isSearchable = _ref6.isSearchable,
    _ref6$options = _ref6.options,
    defaultOptions = _ref6$options === void 0 ? [] : _ref6$options,
    name = _ref6.name,
    onBlur = _ref6.onBlur,
    onChange = _ref6.onChange,
    onClick = _ref6.onClick,
    onCreate = _ref6.onCreate,
    onFocus = _ref6.onFocus,
    onKeyDown = _ref6.onKeyDown,
    _ref6$placeholder = _ref6.placeholder,
    placeholder = _ref6$placeholder === void 0 ? 'Choose from…' : _ref6$placeholder,
    _ref6$renderItem = _ref6.renderItem,
    renderItem = _ref6$renderItem === void 0 ? itemToString : _ref6$renderItem,
    _ref6$renderMultiple = _ref6.renderMultiple,
    renderMultiple = _ref6$renderMultiple === void 0 ? MultipleSelections : _ref6$renderMultiple,
    _ref6$size = _ref6.size,
    size = _ref6$size === void 0 ? 'lg' : _ref6$size,
    type = _ref6.type,
    defaultSelected = _ref6.value,
    variant = _ref6.variant,
    allowUnselectFromList = _ref6.allowUnselectFromList,
    disableCloseOnSelect = _ref6.disableCloseOnSelect,
    rest = _objectWithoutPropertiesLoose(_ref6, _excluded);
  var _useState = useState(''),
    previousInput = _useState[0],
    setPreviousInput = _useState[1];
  var defaultSelecteds = useMemo(function () {
    return getOptionsFromSelected(defaultSelected, defaultOptions);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [defaultSelected]);
  var selectedItem = !isMultiple && defaultSelecteds[0] || null;
  var defaultInputValue = selectedItem ? selectedItem.label : previousInput;

  // We keep 3 things in state:
  // a. selected = currently selected item(s)
  // b. inputValue = text in the select/search box
  // c. options = options in the dropdown
  // d. isOpen = the state of the dropdown menu
  var _useState2 = useState(defaultSelecteds),
    selected = _useState2[0],
    setSelected = _useState2[1];
  var _useState3 = useState(defaultInputValue),
    inputValue = _useState3[0],
    setInputValue = _useState3[1];
  var _useState4 = useState(defaultOptions),
    options = _useState4[0],
    setOptions = _useState4[1];
  var _useState5 = useState(false),
    isOpen = _useState5[0],
    setIsOpen = _useState5[1];

  // Set default isSearchable
  isSearchable = isCreatable || isSearchable;

  // Autofocus
  useEffect(function () {
    if (autoFocus) {
      ref.current.focus();
    }
  }, [autoFocus, ref]);

  // Ensure values are controlled by parent
  useEffect(function () {
    setSelected(defaultSelecteds);
    setInputValue(defaultInputValue);
    setOptions(defaultOptions);
  }, [defaultInputValue, defaultOptions, defaultSelecteds]);

  // Update options when searching
  var handleInputChange = function handleInputChange(value) {
    if (onKeyDown) onKeyDown(value);
    // Update
    if (isSearchable && value !== inputValue) {
      var _options = matchSorter(defaultOptions, value, {
        keys: ['label']
      });
      setPreviousInput(value);
      setInputValue(value);
      setOptions(_options);
    }
  };
  var handleInputKeyDown = function handleInputKeyDown() {
    if (isSearchable && !isOpen) {
      setIsOpen(true);
    }
  };

  // Send event to parent when value(s) changes
  var handleChange = function handleChange(options) {
    var values = getValuesFromOptions(options, defaultOptions);
    var value = isMultiple ? values : values[0];
    var event = createEvent({
      name: name,
      value: isMultiple ? options : options[0]
    });

    // If there are newly-created options, call `onCreate`
    if (isCreatable) {
      var newOptions = getNewOptions(options, defaultOptions);
      if (newOptions.length) {
        onCreate && onCreate(newOptions[0].label, event);
      } else {
        onChange && onChange(value, event);
      }
    } else {
      onChange && onChange(value, event);
    }
  };

  // Update internal state when clicking/adding a select item
  var handleSelect = function handleSelect(option) {
    var newItems;
    var isClearInput;
    if (!option) {
      // If removing option
      newItems = isMultiple ? selected : [];
      isClearInput = true;
    } else {
      // If adding option
      newItems = isMultiple ? getUniqueValue(option, selected, allowUnselectFromList) : [option];
      isClearInput = isMultiple;
    }
    isClearInput && setInputValue('');
    setOptions(defaultOptions);
    setSelected(newItems);
    handleChange(newItems);
    if (!disableCloseOnSelect) {
      setIsOpen(false);
    }
  };
  var handleRemove = function handleRemove(value) {
    var newItems = selected.filter(function (item) {
      return item.value !== value;
    });
    setSelected(newItems);
    handleChange(newItems);
  };
  var handleOuterClick = function handleOuterClick(e) {
    // Reset input value if not selecting a new item
    if (isMultiple && e.selectedItem) {
      setInputValue('');
    } else if (isSearchable && e.selectedItem) {
      setInputValue(e.selectedItem.label);
    }
    setOptions(defaultOptions);
    setIsOpen(false);
  };
  var spacer = getSpacer(defaultOptions);
  var inputContent = getInputValue({
    inputValue: inputValue,
    isMultiple: isMultiple,
    isSearchable: isSearchable,
    options: defaultOptions,
    renderItem: renderItem
  });
  var stateReducer = function stateReducer(state, changes) {
    if (changes.isOpen) {
      return _extends({}, changes, {
        highlightedIndex: options.indexOf(state.selectedItem)
      });
    }
    return changes;
  };
  return /*#__PURE__*/React.createElement(Downshift, {
    inputValue: isSearchable ? inputContent : '',
    isOpen: isOpen,
    itemToString: itemToString,
    onInputValueChange: handleInputChange,
    onOuterClick: handleOuterClick,
    onSelect: handleSelect,
    selectedItem: selectedItem,
    stateReducer: stateReducer
  }, function (_ref7) {
    var clearSelection = _ref7.clearSelection,
      getInputProps = _ref7.getInputProps,
      getItemProps = _ref7.getItemProps,
      getMenuProps = _ref7.getMenuProps,
      getRootProps = _ref7.getRootProps,
      getToggleButtonProps = _ref7.getToggleButtonProps,
      highlightedIndex = _ref7.highlightedIndex,
      openMenu = _ref7.openMenu,
      closeMenu = _ref7.closeMenu;
    var isShowCreate = !!(isCreatable && inputValue && !isValueSelected(inputValue, selected));
    var isShowMenu = isOpen && (options.length || isShowCreate);
    var isShowDeleteIcon = isClearable && inputValue;
    var DeleteIcon = /*#__PURE__*/React.createElement(StyledDropDownIndicator, {
      as: "div",
      size: size
    }, /*#__PURE__*/React.createElement(ClearButton, {
      onClick: clearSelection
    }));
    var ArrowIcon = /*#__PURE__*/React.createElement(StyledDropDownIndicator, _extends({
      disabled: disabled,
      isOpen: isOpen,
      size: size,
      tabIndex: -1
    }, getToggleButtonProps({
      onClick: function onClick() {
        !isOpen ? openMenu() : closeMenu();
        setIsOpen(!isOpen);
      }
    })), /*#__PURE__*/React.createElement(CaretDownIcon, {
      color: "nude.800",
      size: "xs"
    }));
    var handleInputClick = function handleInputClick(e) {
      onClick && onClick(e);
      setIsOpen(!isOpen);
      !isOpen ? openMenu() : closeMenu();
    };
    var rootProps = getRootProps(rest);
    var inputProps = getInputProps(_extends({
      autoComplete: autoComplete,
      autoFocus: autoFocus,
      'data-spacer': spacer || placeholder,
      'data-testid': dataTestId,
      disabled: disabled,
      hasIcon: !!icon,
      id: id,
      name: name,
      onBlur: onBlur,
      onClick: disabled ? undefined : handleInputClick,
      onFocus: onFocus,
      onKeyDown: handleInputKeyDown,
      placeholder: placeholder,
      ref: ref,
      size: size,
      tabIndex: 0,
      variant: isOpen ? 'focused' : variant
    }, rest));
    return /*#__PURE__*/React.createElement(StyledWrapper, rootProps, /*#__PURE__*/React.createElement(StyledInputWrapper, null, isSearchable ? /*#__PURE__*/React.createElement(StyledInput, _extends({
      as: "input",
      type: "text"
    }, inputProps)) : /*#__PURE__*/React.createElement(StyledInput, inputProps, inputContent), icon && /*#__PURE__*/React.createElement(StyledIcon, {
      size: size
    }, icon), /*#__PURE__*/React.createElement(StyledIndicators, {
      size: size
    }, isShowDeleteIcon && DeleteIcon, ArrowIcon)), isShowMenu && /*#__PURE__*/React.createElement(StyledMenu, getMenuProps(), isShowCreate && inputValue.length && /*#__PURE__*/React.createElement(StyledListItem, _extends({
      key: "add"
    }, getItemProps({
      index: options.length,
      isHighlighted: highlightedIndex === options.length,
      item: {
        value: kebabCase(inputValue),
        label: inputValue
      }
    })), "Create \"" + inputValue + "\""), options.map(function (item, index) {
      var isItemSelected = isValueSelected(item.value, selected);
      return /*#__PURE__*/React.createElement(StyledListItem, _extends({
        key: item.value
      }, getItemProps({
        index: index,
        isHighlighted: highlightedIndex === index,
        isSelected: isItemSelected,
        allowUnselectFromList: allowUnselectFromList,
        isMultiple: isMultiple,
        item: item
      })), renderItem(item, isItemSelected));
    })), isMultiple && renderMultiple(selected, handleRemove));
  });
});
Select.displayName = 'Select';
var StyledSelect = StyledWrapper;

export { ListStyles, Select, StyledListItem, StyledSelect };
